import { useDebounced, useTitle } from "modules/common";
import { PanelMenu, PanelMenuItem } from "modules/common/components/PanelMenu";
import { CommunicationRoutes } from "modules/common/constants/routes";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AppRoutes } from "routing";
import { NotificationTemplates } from "./NotificationTemplates";
import { SendNotification } from "./SendNotification";
import { SentNotifications } from "./SentNotifications";

const INPUT_DEBOUNCE_TIME = 500;

const MENU_ITEMS: PanelMenuItem[] = [
    {
        label: "Komunikacija s korisnicima",
        link: CommunicationRoutes.SendNotification,
    },
    {
        label: "Predlošci poruka",
        link: CommunicationRoutes.NotificationTemplates,
    },
    {
        label: "Poslane poruke",
        link: CommunicationRoutes.SentNotifications,
    },
];

export const Communication: React.FC = () => {
    useTitle("Komunikacija s korisnicima - eSavjetnik");

    const { search, pathname } = useLocation();
    const history = useHistory();

    const menuItems = useMemo(() => MENU_ITEMS, []);
    const [selectedMenuItem, setSelectedMenuItem] = useState<PanelMenuItem>(menuItems[0]);
    const [lastSelectedMenuItem, setLastSelectedMenuItem] = useState<PanelMenuItem>(menuItems[0]);
    const [searchFilter, setSearchFilter] = useState("");

    const debouncedSearchFilter = useDebounced(searchFilter, INPUT_DEBOUNCE_TIME);

    useEffect(() => {
        setSearchFilter(debouncedSearchFilter);
    }, [debouncedSearchFilter]);

    useEffect(() => {
        const routeMap: Record<string, string> = {
            [CommunicationRoutes.SendNotification]: CommunicationRoutes.SendNotification,
            [CommunicationRoutes.NotificationTemplates]: CommunicationRoutes.NotificationTemplates,
            [CommunicationRoutes.SentNotifications]: CommunicationRoutes.SentNotifications,
        };

        const targetRoute = routeMap[pathname];
        
        if (targetRoute) {
            const foundMenuItem = menuItems.find(mi => mi.link === targetRoute);
            if (foundMenuItem) {
                setSelectedMenuItem(foundMenuItem);
            }
        } else if (pathname === AppRoutes.Communication) {
            const defaultMenuItem = menuItems[0];
            setSelectedMenuItem(defaultMenuItem);
            history.push({
                pathname: defaultMenuItem.link,
                search: search,
            });
        }
    }, [pathname, menuItems, history, search]);

    useEffect(() => {
        if (selectedMenuItem !== MENU_ITEMS[1]) return;

        const params = new URLSearchParams(search);
        if (!params.get("templateId")) return;

        setSelectedMenuItem(MENU_ITEMS[0]);
    }, [search, selectedMenuItem]);

    return (
        <div className="communication-container">
            <div className="communication-content">
                <PanelMenu
                    isInput={selectedMenuItem.link === CommunicationRoutes.NotificationTemplates}
                    input={searchFilter}
                    setInput={setSearchFilter}
                    hidden={selectedMenuItem.link === CommunicationRoutes.SentNotifications}
                    items={MENU_ITEMS}
                    selectedItem={selectedMenuItem}
                    isRightIcon
                    onItemClicked={item => {
                        if (item === selectedMenuItem) return;

                        setLastSelectedMenuItem(selectedMenuItem);
                        setSelectedMenuItem(item);
                        history.push(item.link);
                    }}
                />

                {selectedMenuItem?.link === CommunicationRoutes.SendNotification && (
                    <SendNotification />
                )}

                {selectedMenuItem?.link === CommunicationRoutes.NotificationTemplates && (
                    <NotificationTemplates
                        searchFilter={debouncedSearchFilter}
                        setSearchFilter={setSearchFilter}
                    />
                )}

                {selectedMenuItem?.link === CommunicationRoutes.SentNotifications && (
                    <SentNotifications onReturn={() => setSelectedMenuItem(lastSelectedMenuItem)} />
                )}
            </div>
        </div>
    );
};
